import { useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { FadeUp } from "@/components/Motion";
import BeeTileBackdrop from "@/components/BeeTileBackdrop";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";

export default function Review() {
  const { t, lang } = useI18n();
  const R = t.review;
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [hover, setHover] = useState(0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) { toast.error(R.error); return; }
    setBusy(true);
    try {
      await api.post("/reviews", { name: name.trim(), rating, text: text.trim(), lang });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || R.error);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-cream font-body placeholder:text-cream/30 transition-colors";

  return (
    <div className="pt-16 pb-40 min-h-screen relative overflow-hidden" data-testid="review-page">
      <Seo title={`${t.review.title} — Madubau Dolomiti`} description={t.seo.review_d} path="/bewertung" />
      <BeeTileBackdrop opacity={0.3} />
      <div className="relative z-10 max-w-2xl mx-auto px-6">
        <FadeUp className="text-center mb-5">
          <div className="font-display text-[10px] tracking-[0.4em] text-gold mb-2">/ {R.eyebrow}</div>
          <h1 className="font-editorial italic font-light text-3xl sm:text-5xl text-cream mb-2">{R.title}</h1>
          <p className="text-cream/60 font-body text-sm leading-relaxed">{R.sub}</p>
        </FadeUp>

        {done ? (
          <FadeUp className="text-center border border-gold/30 bg-gold/5 p-12" data-testid="review-thanks">
            <div className="flex justify-center gap-1 mb-6">
              {[...Array(rating)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-gold text-gold" />
              ))}
            </div>
            <div className="font-editorial italic text-3xl text-gold mb-3">{R.thanks_t}</div>
            <p className="text-cream/60 font-body">{R.thanks_s}</p>
          </FadeUp>
        ) : (
          <FadeUp>
            <form onSubmit={submit} className="space-y-5" data-testid="review-form">
              <div className="text-center">
                <div className="font-display text-[10px] tracking-[0.3em] text-cream/50 mb-2">{R.rating_label}</div>
                <div className="flex justify-center gap-2" role="radiogroup" aria-label={R.rating_label}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      data-testid={`review-star-${n}`}
                      aria-label={`${n} / 5`}
                      className="p-1 transition-transform hover:scale-125"
                    >
                      <Star className={`w-8 h-8 transition-colors ${(hover || rating) >= n ? "fill-gold text-gold" : "text-cream/25"}`} />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="font-display text-[10px] tracking-[0.3em] text-cream/50 block mb-1">{R.name_label}</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={80}
                  data-testid="review-name-input"
                  className={inputCls}
                  placeholder={R.name_ph}
                />
              </div>

              <div>
                <label className="font-display text-[10px] tracking-[0.3em] text-cream/50 block mb-1">{R.text_label}</label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={2}
                  maxLength={1000}
                  data-testid="review-text-input"
                  className={`${inputCls} resize-none`}
                  placeholder={R.text_ph}
                />
              </div>

              <button
                type="submit"
                disabled={busy}
                data-testid="review-submit-btn"
                className="w-full font-display text-[11px] tracking-[0.35em] bg-gold text-black py-4 hover:bg-cream transition-colors disabled:opacity-50"
              >
                {busy ? R.sending : R.submit}
              </button>
            </form>
          </FadeUp>
        )}
      </div>
    </div>
  );
}
